// Shared component (TS-2.5.03). Owner ruling 2026-10-03: the six CLOSED fields (project Status,
// project Team, milestone Completion, milestone Team, note Type, note Status) were each an
// `<input list=...>` + `<datalist>` pair. A browser datalist filters its suggestions by the
// control's CURRENT TEXT, so once a value is set the user sees only that one value typed back at
// them and has no way to discover what else the list offers - effectively invisible vocabulary.
// This renders a native `<select>` instead: the full active vocabulary is always visible, and
// native keyboard/AT (type-ahead, arrow keys, screen-reader list semantics) comes for free - no
// custom popup to build or maintain.
//
// A STORED VALUE IS SHOWN AS WHAT IT MEANS, AND NEVER REWRITTEN UNLESS THE USER PICKS. The
// workbook stores most of these values in lower case ("not started", "completed", "risk") - on the
// live data 71 of 73 project statuses, all 131 milestone completions and every note type and status
// differ from their canonical label only by case or by being a seeded alias. The roadmap already
// resolves them case-insensitively through `aliases`, so the select does the same: a value matching
// an active option's canonical label or alias (trimmed, case-insensitive) shows THAT option as
// selected. Only the DOM value is mapped - `onChange` fires only when the user picks, so the draft
// keeps the stored text and saving the record without touching this field writes it back
// byte-identical; adopting the canonical spelling is the user's own act of choosing it.
//
// A value matching nothing active (true spelling drift, or a value an Admin has since retired) is
// rendered as one extra "(not in list)" option at the top, selected, with `value={value}`, so it
// stays visible and round-trips unchanged too.
//
// None of this narrows what the API accepts: the workbook IMPORT path still accepts an unlisted
// value with a warning rather than a refusal (D-195), and the save API still accepts and echoes
// back an unlisted value unchanged (D-229 item 3) - this `<select>` is a UI-only constraint drawn
// by the owner's ruling, layered on top of an API that was never narrowed. New vocabulary values
// are added by an Admin under Admin > Reference data, not from this control.
import type { ReferenceValue } from "../api/generated/contracts";
import { formFieldControlProps } from "./formFieldControlProps";
import { strings } from "../strings";

export interface VocabularySelectProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly options: readonly ReferenceValue[];
  readonly allowEmpty?: boolean;
  /** Native `required`: with the empty "— None —" option selected, the enclosing form will not submit. */
  readonly required?: boolean;
  readonly description?: string;
  readonly error?: string | null;
}

const S = strings.common;

export function VocabularySelect({
  id,
  value,
  onChange,
  options,
  allowEmpty = true,
  required,
  description,
  error,
}: VocabularySelectProps) {
  const activeOptions = [...options]
    .filter((option) => option.isActive)
    .sort((left, right) => left.displayOrder - right.displayOrder);

  const matched = recognise(value, activeOptions);
  const showNotInListOption = value.trim().length > 0 && matched === null;
  const shownValue = matched === null ? value : matched.canonicalLabel;

  return (
    <select
      value={shownValue}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      {...formFieldControlProps(id, description, error)}
    >
      {allowEmpty && <option value="">{S.selectNone}</option>}
      {showNotInListOption && (
        <option value={value}>{S.notInList(value)}</option>
      )}
      {activeOptions.map((option) => (
        <option key={option.key} value={option.canonicalLabel}>
          {option.canonicalLabel}
        </option>
      ))}
    </select>
  );
}

/** The active option a stored value means: exact canonical label first, then canonical or alias, trimmed and case-insensitive. */
function recognise(value: string, options: readonly ReferenceValue[]): ReferenceValue | null {
  const exact = options.find((option) => option.canonicalLabel === value);
  if (exact) return exact;

  const folded = value.trim().toLowerCase();
  if (folded.length === 0) return null;

  return (
    options.find(
      (option) =>
        option.canonicalLabel.toLowerCase() === folded ||
        option.aliases.some((alias) => alias.trim().toLowerCase() === folded),
    ) ?? null
  );
}
